import { createElement, useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { CopilotUsageCard } from "./copilot-usage-card.js";
import { SessionAccountViewSchema } from "./session-accounts-remote.js";
import { useSessionContinuationSwitch } from "./session-continuation-ui.js";
const slot = 'conversation.composer.dock';
const noop = () => { };
const emptySubscribe = () => noop;
function record(value) {
    return typeof value === 'object' && value !== null;
}
function isSlots(value) {
    return record(value) && typeof value.spec === 'function'
        && typeof value.inject === 'function' && typeof value.register === 'function';
}
function isRemote(value) {
    return record(value) && typeof value.get === 'function' && typeof value.refresh === 'function';
}
function isSessionRemote(value) {
    return record(value) && typeof value.get === 'function' && typeof value.set === 'function'
        && typeof value.refreshIdentity === 'function' && typeof value.ensureIdentity === 'function'
        && typeof value.usage === 'function' && typeof value.refreshUsage === 'function';
}
function isContinuationRemote(value) {
    return record(value) && typeof value.get === 'function' && typeof value.set === 'function'
        && (value.authorizeNext === undefined || typeof value.authorizeNext === 'function')
        && (value.defaults === undefined || typeof value.defaults === 'function')
        && (value.setDefault === undefined || typeof value.setDefault === 'function');
}
function isLocale(value) {
    return record(value) && typeof value.getLocale === 'function' && typeof value.subscribe === 'function';
}
function isRuntime(value) {
    return record(value) && typeof value.sessionId === 'string' && value.sessionId !== ''
        && typeof value.useSession === 'function' && typeof value.useProjection === 'function';
}
function selection(value) {
    return record(value) && typeof value.provider === 'string' && value.provider.trim() !== ''
        && typeof value.model === 'string' && value.model.trim() !== '';
}
/**
 * Verified alpha.2 wire projection: next = pending ?? lastUsed; lastUsed is
 * request/header.config. Never consult the future global default or the model
 * catalog. A blank Session without explicit selection waits for that evidence.
 */
function effectiveCopilot(projection) {
    if (!record(projection) || !(projection.lastUsed === null || selection(projection.lastUsed))
        || !(projection.next === null || selection(projection.next)))
        return undefined;
    const next = projection.next;
    if (!selection(next))
        return undefined;
    return next.provider === 'github-copilot' || next.provider === 'github-copilot-preview' ? next : undefined;
}
function Surface({ runtime, remote, accountsRemote, sessionRemote, continuationRemote, locale, diagnostic }) {
    const valid = runtime.useSession(session => record(session) && session.sessionId === runtime.sessionId
        && session.removed === false && session.openState === 'open');
    const projection = runtime.useProjection('modelSelection');
    const running = runtime.useSession(value => !record(value) || value.running !== false);
    const language = useSyncExternalStore(locale === undefined ? emptySubscribe : listener => locale.subscribe(listener), () => locale?.getLocale().active ?? 'en', () => 'en');
    const current = effectiveCopilot(projection);
    const changesAccount = useCallback(async (accountId) => {
        const result = await sessionRemote?.get(runtime.sessionId);
        const parsed = result?.ok ? SessionAccountViewSchema.safeParse(result.value) : undefined;
        if (!parsed?.success)
            throw new Error('COPILOT_SESSION_ACCOUNTS_REMOTE_UNAVAILABLE');
        return (accountId ?? parsed.data.globalAccountId) !== parsed.data.accountId;
    }, [sessionRemote, runtime.sessionId]);
    const continuation = useSessionContinuationSwitch({ sessionId: runtime.sessionId,
        remote: continuationRemote, locale: language, changesAccount, running });
    const bound = useMemo(() => {
        if (sessionRemote === undefined)
            return undefined;
        const id = runtime.sessionId;
        return {
            usage: { get: () => sessionRemote.usage(id), refresh: () => sessionRemote.refreshUsage(id) },
            identity: {
                get: async () => {
                    const result = await sessionRemote.get(id);
                    return result.ok ? { ok: true, value: result.value.accounts } : result;
                },
                refreshIdentity: async () => {
                    const result = await sessionRemote.refreshIdentity(id);
                    return result.ok ? { ok: true, value: result.value.accounts } : result;
                },
                ensureIdentity: async () => {
                    const result = await sessionRemote.ensureIdentity(id);
                    return result.ok ? { ok: true, value: result.value.accounts } : result;
                },
            },
            account: { get: () => sessionRemote.get(id),
                set: (account, revision) => sessionRemote.set(id, account, revision) },
        };
    }, [sessionRemote, runtime.sessionId]);
    useEffect(() => {
        if (projection === undefined)
            diagnostic('COPILOT_USAGE_MODEL_PROJECTION_UNAVAILABLE');
    }, [projection, diagnostic]);
    if (!valid || current === undefined)
        return null;
    const contextKey = JSON.stringify([runtime.sessionId, current.provider, current.model]);
    return createElement(CopilotUsageCard, { key: contextKey, contextKey,
        remote: current.provider === 'github-copilot-preview' ? bound?.usage : remote,
        accountsRemote: current.provider === 'github-copilot-preview' ? bound?.identity : accountsRemote,
        sessionAccount: current.provider === 'github-copilot-preview' ? bound?.account : undefined, locale: language,
        continuation: current.provider === 'github-copilot-preview' ? continuation.content : undefined,
        beforeAccountChange: current.provider === 'github-copilot-preview' ? continuation.beforeAccountChange : undefined,
    });
}
/** Public additive dock only; native composer, ContextMeter and other features stay owned by Core. */
export function registerCopilotUsageUi(ctx) {
    const diagnosed = new Set();
    const diagnostic = (code) => {
        if (!diagnosed.has(code)) {
            diagnosed.add(code);
            ctx.logger.warn(`[github-copilot] ${code}`);
        }
    };
    const candidate = ctx.slots;
    if (!isSlots(candidate)) {
        diagnostic('COPILOT_USAGE_SLOT_UNAVAILABLE');
        return noop;
    }
    const slots = candidate;
    // Resolve a traced Remote once, not on each render or Session-model update.
    let remote;
    let accountsRemote;
    let sessionRemote;
    let continuationRemote;
    try {
        const namespaces = ctx.remote;
        const face = record(namespaces) ? namespaces.githubCopilotUsage : undefined;
        if (isRemote(face)) {
            remote = face;
        }
        else
            diagnostic('COPILOT_USAGE_REMOTE_UNAVAILABLE');
        const accounts = record(namespaces) ? namespaces.githubCopilotAccounts : undefined;
        if (record(accounts) && typeof accounts.get === 'function' && typeof accounts.refreshIdentity === 'function'
            && typeof accounts.ensureIdentity === 'function') {
            accountsRemote = accounts;
        }
        else
            diagnostic('COPILOT_ACCOUNTS_REMOTE_UNAVAILABLE');
        const session = record(namespaces) ? namespaces.githubCopilotSessionAccount : undefined;
        if (isSessionRemote(session))
            sessionRemote = session;
        else
            diagnostic('COPILOT_SESSION_ACCOUNTS_REMOTE_UNAVAILABLE');
        const continuation = record(namespaces) ? namespaces.githubCopilotSessionContinuation : undefined;
        if (isContinuationRemote(continuation)) {
            continuationRemote = continuation;
        }
        else
            diagnostic('COPILOT_CONTINUATION_REMOTE_UNAVAILABLE');
    }
    catch {
        diagnostic('COPILOT_USAGE_REMOTE_UNAVAILABLE');
    }
    const localeCandidate = ctx.get('locale');
    const locale = isLocale(localeCandidate) ? localeCandidate : undefined;
    let active = true;
    let remove;
    let injection = noop;
    try {
        if (slots.spec(slot) === undefined)
            diagnostic('COPILOT_USAGE_SLOT_UNAVAILABLE');
        injection = slots.inject(slot, () => {
            if (!active)
                return noop;
            const spec = slots.spec(slot);
            if (spec?.kind !== 'list' || spec.scope !== 'session') {
                diagnostic('COPILOT_USAGE_SLOT_UNAVAILABLE');
                return noop;
            }
            try {
                const dispose = slots.register({ name: slot, id: 'github-copilot-usage', order: 20 }, props => {
                    if (!isRuntime(props)) {
                        diagnostic('COPILOT_USAGE_SESSION_RUNTIME_UNAVAILABLE');
                        return null;
                    }
                    return createElement(Surface, {
                        runtime: props, remote, accountsRemote, sessionRemote, continuationRemote, locale, diagnostic,
                    });
                });
                let removed = false;
                const cleanup = () => { if (!removed) {
                    removed = true;
                    dispose();
                } };
                remove = cleanup;
                return cleanup;
            }
            catch {
                diagnostic('COPILOT_USAGE_SLOT_UNAVAILABLE');
                return noop;
            }
        });
    }
    catch {
        diagnostic('COPILOT_USAGE_SLOT_UNAVAILABLE');
    }
    return () => {
        if (!active)
            return;
        active = false;
        injection();
        remove?.();
    };
}
//# sourceMappingURL=copilot-usage-ui.js.map